import React from "react";
import { NextCriticalActionCard } from "../../components/dealroom/NextCriticalActionCard";

/* ─────────────────────────────────────────────────────────────
   MarketingIntelligence · Phase 3 Homepage
   ------------------------------------------------------------
   Dark section that surfaces the ChainFlow intelligence model:

       WHAT →  WHY →  NEXT ACTION

   Real primitive reused (unmodified):
     • NextCriticalActionCard  (from /components/dealroom)

   No exchange-probability, no fake AI accuracy, no invented
   certainty metrics. The card only renders states the real
   production component computes from `deal.stage` +
   `openTasks[].due_at`.

   Dark section framing mirrors the Phase 1 hero pattern:
   dark navy background hosts the real light-mode product
   cards, so the eye reads the product directly.
   ───────────────────────────────────────────────────────────── */

const DAY = 24 * 60 * 60 * 1000;

const buildPrimaryDeal = () => ({
  id: "mkt-int-01",
  stage: "enquiries",
  targetExchange: new Date(Date.now() + 9 * DAY).toISOString(),
  activity: [],
});

const buildPrimaryTasks = () => {
  const now = Date.now();
  return [
    {
      id: "mkt-int-t1",
      title: "Chase management pack — 23 Hallings Wharf",
      due_at: new Date(now - 3 * DAY).toISOString(),
      assigned_to: null,
      kickoff_stage_hint: "enquiries",
    },
    {
      id: "mkt-int-t2",
      title: "Reply on outstanding enquiries — 3 items",
      due_at: new Date(now - 1 * DAY).toISOString(),
      assigned_to: null,
      kickoff_stage_hint: "enquiries",
    },
  ];
};

const buildSecondaryDeal = () => ({
  id: "mkt-int-02",
  stage: "searches",
  targetExchange: new Date(Date.now() + 18 * DAY).toISOString(),
  activity: [],
});

const buildSecondaryTasks = () => {
  const now = Date.now();
  return [
    {
      id: "mkt-int-t3",
      title: "Chase local authority searches — 10 High Street, Richmond",
      due_at: new Date(now - 8 * DAY).toISOString(),
      assigned_to: null,
      kickoff_stage_hint: "searches",
    },
  ];
};

export const MarketingIntelligence = () => {
  const primaryDeal = React.useMemo(buildPrimaryDeal, []);
  const primaryTasks = React.useMemo(buildPrimaryTasks, []);
  const secondaryDeal = React.useMemo(buildSecondaryDeal, []);
  const secondaryTasks = React.useMemo(buildSecondaryTasks, []);

  return (
    <section
      id="intelligence"
      className="relative overflow-hidden"
      data-testid="landing-intelligence"
      style={{
        background:
          "radial-gradient(1100px 700px at 90% 0%, rgba(124,58,237,0.24), transparent 60%)," +
          "radial-gradient(900px 700px at 10% 110%, rgba(79,107,255,0.20), transparent 55%)," +
          "linear-gradient(180deg, #0B1130 0%, #0A0E28 55%, #080B22 100%)",
      }}
    >
      {/* Ambient glow accents (mirror hero pattern) */}
      <span
        aria-hidden="true"
        className="absolute -top-16 right-[-160px] w-[720px] h-[560px] rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(139,92,246,0.18), transparent 70%)" }}
      />
      {/* Subtle grid overlay */}
      <span
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.28) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.28) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black 30%, transparent 75%)",
        }}
      />

      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-16 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] gap-8 lg:gap-14 items-start">
          {/* Copy column */}
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-violet-300">
              Operational intelligence
            </p>
            <h2 className="font-display text-[32px] sm:text-[40px] lg:text-[52px] font-bold tracking-[-0.028em] text-white leading-[1.02] mt-4">
              Don't just know<br />
              what's happening.<br />
              <span
                style={{
                  background: "linear-gradient(90deg, #A5B4FC 0%, #C4B5FD 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Know what to do next.
              </span>
            </h2>
            <p className="text-[14px] sm:text-[15.5px] text-slate-300 leading-relaxed mt-5 max-w-[520px]">
              ChainFlow surfaces what needs attention, why it matters and the next action to take —
              so progression teams can act before issues drift.
            </p>

            {/* WHAT → WHY → NEXT ACTION legend — restrained,
                lives beside the real cards so the pattern reads
                even at a glance. */}
            <ul className="mt-8 space-y-3 max-w-[420px]" data-testid="landing-intelligence-legend">
              {[
                ["What",        "The transaction that needs attention"],
                ["Why",         "The reason it's blocked or slowing"],
                ["Next action", "The single step to move it forward"],
              ].map(([label, body]) => (
                <li key={label} className="flex items-start gap-3">
                  <span className="mt-[3px] w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
                  <div>
                    <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-300">
                      {label}
                    </p>
                    <p className="text-[13px] text-slate-300 mt-0.5 leading-snug">{body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Real product surface — 2 stacked NextCriticalActionCard
              instances. Cards render in their production light styling,
              floating on the dark section background. */}
          <div className="space-y-5">
            <div data-testid="landing-intelligence-primary">
              <NextCriticalActionCard deal={primaryDeal} openTasks={primaryTasks} />
            </div>
            <div data-testid="landing-intelligence-secondary">
              <NextCriticalActionCard deal={secondaryDeal} openTasks={secondaryTasks} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketingIntelligence;
